import React, { useState } from 'react';
import { X, ArrowRight, Clock, TrendingUp, TrendingDown, Target, ChevronLeft, ChevronRight } from 'lucide-react';
import { isBuyDirection } from '../../utils/tradeCalculations';
import { detectTradingSession, SESSION_COLORS } from '../../utils/tradingSessions';

function toTvSymbol(symbol) {
  const s = (symbol || '').toUpperCase().trim();
  if (s === 'XAUUSD') return 'TVC:GOLD';
  if (s === 'XAGUSD') return 'TVC:SILVER';
  if (s === 'US30' || s === 'DJ30') return 'FOREXCOM:DJI';
  if (s === 'NAS100' || s === 'USTEC') return 'FOREXCOM:NSXUSD';
  if (s === 'SPX500' || s === 'SP500') return 'FOREXCOM:SPX500';
  if (s.length === 6) return 'FX:' + s;
  return s || 'FX:EURUSD';
}

function formatDuration(openTime, closeTime) {
  if (!openTime || !closeTime) return null;
  const ms = new Date(closeTime) - new Date(openTime);
  if (ms < 0) return null;
  const totalSec = Math.floor(ms / 1000);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatPriceChange(entry, exit, direction) {
  if (!entry || !exit) return null;
  const change = isBuyDirection(direction) ? exit - entry : entry - exit;
  const pct = ((change / entry) * 100).toFixed(3);
  return { change: change.toFixed(5), pct, positive: change >= 0 };
}

export const TradeReplayModal = ({ trade, onClose, trades = [] }) => {
  const [showMarkups, setShowMarkups] = useState(true);

  // Find adjacent trades for navigation
  const currentIdx = trades.findIndex(t => t.id === trade.id);
  const prevTrade = currentIdx > 0 ? trades[currentIdx - 1] : null;
  const nextTrade = currentIdx >= 0 && currentIdx < trades.length - 1 ? trades[currentIdx + 1] : null;
  const [activeTrade, setActiveTrade] = useState(trade);

  const tvSymbol = toTvSymbol(activeTrade.symbol);
  const entryDate = activeTrade.openTime || activeTrade.trade_date;
  const session = activeTrade.trading_session || detectTradingSession(activeTrade.openTime);
  const duration = formatDuration(activeTrade.openTime, activeTrade.closeTime);
  const priceChange = formatPriceChange(
    Number(activeTrade.entry_price),
    Number(activeTrade.exit_price),
    activeTrade.direction
  );
  const profit = Number(activeTrade.profit) || 0;
  const isWin = profit > 0;

  // Build time window: 4 hours before entry → 2 hours after exit (or 6h after entry)
  const entryTs = entryDate ? Math.floor(new Date(entryDate).getTime() / 1000) : null;
  const exitTs = activeTrade.closeTime ? Math.floor(new Date(activeTrade.closeTime).getTime() / 1000) : null;
  const from = entryTs ? entryTs - 4 * 3600 : null;
  const to = entryTs ? (exitTs ? exitTs + 2 * 3600 : entryTs + 6 * 3600) : null;

  let iframeSrc = `/tradingview-widget.html?symbol=${encodeURIComponent(tvSymbol)}`;
  if (from && to) iframeSrc += `&from=${from}&to=${to}`;

  const navigateTo = (t) => {
    if (t) setActiveTrade(t);
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center"
      style={{ zIndex: 9999 }}
    >
      <div
        className="bg-card border border-border rounded-lg shadow-xl overflow-hidden flex flex-col"
        style={{ width: '95vw', height: '92vh' }}
      >
        {/* Header */}
        <div className="flex-shrink-0 p-3 border-b border-border flex justify-between items-center gap-2">
          <div className="flex items-center gap-3 flex-wrap min-w-0">
            {/* Trade navigation */}
            {trades.length > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => navigateTo(prevTrade)}
                  disabled={!prevTrade}
                  className="p-1 rounded hover:bg-muted disabled:opacity-30"
                  title="Previous trade"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-muted-foreground">
                  {currentIdx + 1}/{trades.length}
                </span>
                <button
                  onClick={() => navigateTo(nextTrade)}
                  disabled={!nextTrade}
                  className="p-1 rounded hover:bg-muted disabled:opacity-30"
                  title="Next trade"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            <span className={`px-2 py-1 rounded text-xs font-semibold ${
              isBuyDirection(activeTrade.direction) ? 'bg-green-700/80 text-green-200' : 'bg-red-700/80 text-red-200'
            }`}>
              {activeTrade.direction}
            </span>
            <span className="text-xl font-bold">{activeTrade.symbol}</span>

            {/* Session badge */}
            {session && (
              <span
                className="px-2 py-1 rounded text-xs font-semibold"
                style={{ backgroundColor: `${SESSION_COLORS[session]}20`, color: SESSION_COLORS[session], border: `1px solid ${SESSION_COLORS[session]}40` }}
              >
                {session} Session
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowMarkups(!showMarkups)}
              className={`px-2 sm:px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                showMarkups ? 'bg-primary/20 text-primary border border-primary/40' : 'bg-muted text-muted-foreground'
              }`}
            >
              <span className="hidden sm:inline">{showMarkups ? 'Hide' : 'Show'} </span>Markups
            </button>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground p-2 flex-shrink-0"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Trade Highlights Bar */}
        {showMarkups && (
          <div className="flex-shrink-0 px-4 py-2 bg-muted/50 border-b border-border">
            <div className="flex items-center gap-6 flex-wrap text-sm">
              {/* Entry / Exit */}
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-blue-400" />
                <span className="text-muted-foreground">Entry:</span>
                <span className="font-mono font-semibold">{activeTrade.entry_price}</span>
                {activeTrade.exit_price && (
                  <>
                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Exit:</span>
                    <span className="font-mono font-semibold">{activeTrade.exit_price}</span>
                  </>
                )}
              </div>

              {/* Price Change */}
              {priceChange && (
                <div className="flex items-center gap-1">
                  {priceChange.positive
                    ? <TrendingUp className="w-4 h-4 text-green-400" />
                    : <TrendingDown className="w-4 h-4 text-red-400" />
                  }
                  <span className={priceChange.positive ? 'text-green-400' : 'text-red-400'}>
                    {priceChange.positive ? '+' : ''}{priceChange.pct}%
                  </span>
                </div>
              )}

              {/* P/L */}
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">P/L:</span>
                <span className={`font-semibold ${isWin ? 'text-green-400' : profit < 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                  {isWin ? '+' : ''}{profit.toFixed(2)}
                </span>
              </div>

              {/* Duration */}
              {duration && (
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Hold:</span>
                  <span className="font-medium">{duration}</span>
                </div>
              )}

              {/* Pips */}
              {activeTrade.pips != null && Number(activeTrade.pips) !== 0 && (
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">Pips:</span>
                  <span className={`font-semibold ${Number(activeTrade.pips) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {Number(activeTrade.pips) > 0 ? '+' : ''}{Number(activeTrade.pips).toFixed(1)}
                  </span>
                </div>
              )}

              {/* Time */}
              {entryDate && (
                <div className="flex items-center gap-1 text-muted-foreground text-xs">
                  {new Date(entryDate).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TradingView chart */}
        <iframe
          src={iframeSrc}
          title="Trade Replay"
          allow="fullscreen"
          style={{ flex: 1, width: '100%', border: 'none', display: 'block', minHeight: 0 }}
        />
      </div>
    </div>
  );
};
